import * as React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";

import AppTheme from "./theme/AppTheme";
import { Header, SideMenu } from "./components/Layout";
import {
  HomePage,
  TransactionsPage,
  AccountsPage,
  ReportsPage,
  BudgetPage,
  GoalsPage,
  SharedJarsPage,
  LoginPage,
} from "./pages";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ColorModeProvider } from "./contexts/ColorModeContext";
import ErrorBoundary from "./components/Common/ErrorBoundary";


// Route guard component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Create a wrapper component that uses hooks inside Router context
function DashboardContent(props: { disableCustomTheme?: boolean }) {
  const location = useLocation();
  const [currentPage, setCurrentPage] = React.useState("Home");
  const [headerActions, setHeaderActions] = React.useState<React.ReactNode>(null);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        {/* Sidebar — full viewport height */}
        <SideMenu currentPage={currentPage} onSelectPage={setCurrentPage} />

        {/* Right column: header + main content */}
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, height: '100vh', overflow: "hidden" }}>
          <Header currentPath={location.pathname} actionsSlot={headerActions} />

          <Box
            component="main"
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minHeight: 0,
              backgroundColor: theme.palette.background.default,
              overflow: "hidden",
            })}
          >
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box',
                width: "100%",
              }}
            >
              <Routes>
                <Route path="/" element={<ErrorBoundary><HomePage /></ErrorBoundary>} />
                <Route path="/transactions" element={<ErrorBoundary><TransactionsPage setHeaderActions={setHeaderActions} /></ErrorBoundary>} />
                <Route path="/accounts" element={<ErrorBoundary><AccountsPage setHeaderActions={setHeaderActions} /></ErrorBoundary>} />
                <Route path="/reports" element={<ErrorBoundary><ReportsPage /></ErrorBoundary>} />
                <Route path="/budget" element={<ErrorBoundary><BudgetPage /></ErrorBoundary>} />
                <Route path="/goals" element={<ErrorBoundary><GoalsPage /></ErrorBoundary>} />
                <Route path="/jars" element={<ErrorBoundary><SharedJarsPage setHeaderActions={setHeaderActions} /></ErrorBoundary>} />
              </Routes>
            </Box>
          </Box>
        </Box>
      </Box>
    </AppTheme>
  );
}

export default function Dashboard(props: { disableCustomTheme?: boolean }) {
  return (
    <ColorModeProvider>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <DashboardContent {...props} />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </Router>
    </ColorModeProvider>
  );
}
